/**
 * Formatting Utilities
 * Centralized formatting functions for dates, currency, and addresses.
 * 
 * V1 REQUIREMENT: All timestamps MUST be displayed in America/New_York (ET)
 * DO NOT use toLocaleDateString() or toLocaleString() without explicit timezone
 * 
 * Extracted from App.js for better code organization.
 */

import { formatInTimeZone } from 'date-fns-tz';
import { format } from 'date-fns';

// Account timezone configuration - U.S. Eastern Time
// V1 REQUIREMENT: Single consistent timezone for all operations
export const ACCOUNT_TZ = 'America/New_York';

/**
 * Ensure timestamp is treated as UTC
 * Backend returns timestamps without 'Z' suffix, which JavaScript interprets as local time
 * This helper ensures consistent UTC interpretation
 * @param {string|Date} timestamp - ISO 8601 timestamp (with or without Z)
 * @returns {string} ISO timestamp with Z suffix for UTC
 */
const ensureUtcTimestamp = (timestamp) => {
  if (!timestamp) return null;
  if (timestamp instanceof Date) return timestamp.toISOString();
  
  // If it's a string without Z or timezone offset, append Z to force UTC interpretation
  const str = String(timestamp);
  if (!str.endsWith('Z') && !str.match(/[+-]\d{2}:\d{2}$/)) {
    return str + 'Z';
  }
  return str;
};

/**
 * Format UTC timestamp to Eastern Time (ET) - Full datetime with seconds
 * Uses date-fns-tz for reliable timezone conversion
 * @param {string|Date} isoUtc - ISO 8601 timestamp or Date object
 * @param {boolean} includeSeconds - Whether to include seconds (default: true)
 * @returns {string} Formatted timestamp with " ET" suffix
 */
export const formatUtcToET = (isoUtc, includeSeconds = true) => {
  if (!isoUtc) return 'N/A';
  
  try {
    const utcTimestamp = ensureUtcTimestamp(isoUtc);
    const formatString = includeSeconds ? 'MM/dd/yyyy h:mm:ss a' : 'MM/dd/yyyy h:mm a';
    return formatInTimeZone(utcTimestamp, ACCOUNT_TZ, formatString) + ' ET';
  } catch (error) {
    console.error('Error formatting timestamp to ET:', error, isoUtc);
    return 'Invalid date';
  }
};

// Alias for backward compatibility
export const formatTimestamp = formatUtcToET;

/**
 * Format datetime without seconds - common for UI displays
 * @param {string|Date} isoUtc - ISO 8601 timestamp or Date object
 * @returns {string} Formatted as "MM/dd/yyyy hh:mm AM/PM ET"
 */
export const formatDateTimeET = (isoUtc) => {
  if (!isoUtc) return 'N/A';
  try {
    const utcTimestamp = ensureUtcTimestamp(isoUtc);
    return formatInTimeZone(utcTimestamp, ACCOUNT_TZ, 'MM/dd/yyyy h:mm a') + ' ET';
  } catch (error) {
    console.error('Error formatting datetime to ET:', error, isoUtc);
    return 'Invalid date';
  }
};

/**
 * Format date only (no time) in ET
 * @param {string|Date} isoUtc - ISO 8601 timestamp or Date object
 * @returns {string} Formatted as "MM/dd/yyyy"
 */
export const formatDateOnlyET = (isoUtc) => {
  if (!isoUtc) return 'N/A';
  try {
    const utcTimestamp = ensureUtcTimestamp(isoUtc);
    return formatInTimeZone(utcTimestamp, ACCOUNT_TZ, 'MM/dd/yyyy');
  } catch (error) {
    console.error('Error formatting date to ET:', error, isoUtc);
    return 'Invalid date';
  }
};

/**
 * Format short date (Month Day, Year) in ET
 * @param {string|Date} isoUtc - ISO 8601 timestamp or Date object
 * @returns {string} Formatted as "Jan 15, 2025"
 */
export const formatDateShortET = (isoUtc) => {
  if (!isoUtc) return 'N/A';
  try {
    const utcTimestamp = ensureUtcTimestamp(isoUtc);
    return formatInTimeZone(utcTimestamp, ACCOUNT_TZ, 'MMM d, yyyy');
  } catch (error) {
    console.error('Error formatting short date to ET:', error, isoUtc);
    return 'Invalid date';
  }
};

/**
 * Format a CALENDAR-ONLY date (no timezone semantics).
 *
 * Re-exported from `./formatCalendarDate` (kept as its own module so Jest
 * can unit-test it without pulling in `date-fns-tz`, which uses ESM and
 * trips CRA's default Jest transform).
 *
 * Use this for date-only API fields such as leave `start_date`,
 * `end_date`, and block-date ranges. See `./formatCalendarDate.js` for
 * the full rationale + JSDoc.
 */
export { formatCalendarDate, parseCalendarDate } from './formatCalendarDate';

/**
 * Format short date with time in ET
 * @param {string|Date} isoUtc - ISO 8601 timestamp or Date object
 * @returns {string} Formatted as "Jan 15, 2025 3:30 PM ET"
 */
export const formatDateTimeShortET = (isoUtc) => {
  if (!isoUtc) return 'N/A';
  try {
    const utcTimestamp = ensureUtcTimestamp(isoUtc);
    return formatInTimeZone(utcTimestamp, ACCOUNT_TZ, 'MMM d, yyyy h:mm a') + ' ET';
  } catch (error) {
    console.error('Error formatting short datetime to ET:', error, isoUtc);
    return 'Invalid date';
  }
};



/**
 * Format relative time with fallback to date (for activity feeds)
 * Shows "Just now", "5m ago", "2h ago" for recent, then full date for older
 * @param {string|Date} isoUtc - ISO 8601 timestamp or Date object
 * @returns {string} Relative time or formatted date in ET
 */
export const formatRelativeTimeET = (isoUtc) => {
  if (!isoUtc) return 'N/A';
  
  try {
    const utcTimestamp = ensureUtcTimestamp(isoUtc);
    const date = new Date(utcTimestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    
    // For older timestamps, show full date in ET
    return formatInTimeZone(utcTimestamp, ACCOUNT_TZ, 'MMM d, h:mm a') + ' ET';
  } catch (error) {
    console.error('Error formatting relative time:', error, isoUtc);
    return 'Invalid date';
  }
};

/**
 * Format time only in ET (for activity logs, comments)
 * @param {string|Date} isoUtc - ISO 8601 timestamp or Date object
 * @returns {string} Formatted as "3:30:45 PM ET"
 */
export const formatTimeOnlyET = (isoUtc) => {
  if (!isoUtc) return 'N/A';
  try {
    const utcTimestamp = ensureUtcTimestamp(isoUtc);
    return formatInTimeZone(utcTimestamp, ACCOUNT_TZ, 'h:mm:ss a') + ' ET';
  } catch (error) {
    console.error('Error formatting time to ET:', error, isoUtc);
    return 'Invalid date';
  }
};

/**
 * Format for chart axis labels (short month + day)
 * @param {string|Date|number} isoUtc - ISO 8601 timestamp, Date object, or timestamp
 * @returns {string} Formatted as "Jan 15"
 */
export const formatChartDateET = (isoUtc) => {
  if (!isoUtc) return '';
  try {
    const utcTimestamp = ensureUtcTimestamp(isoUtc);
    return formatInTimeZone(utcTimestamp, ACCOUNT_TZ, 'MMM d');
  } catch (error) {
    return '';
  }
};

/**
 * Format weekday short name in ET
 * @param {string|Date} isoUtc - ISO 8601 timestamp or Date object
 * @returns {string} Formatted as "Mon", "Tue", etc.
 */
export const formatWeekdayShortET = (isoUtc) => {
  if (!isoUtc) return '';
  try {
    const utcTimestamp = ensureUtcTimestamp(isoUtc);
    return formatInTimeZone(utcTimestamp, ACCOUNT_TZ, 'EEE');
  } catch (error) {
    return '';
  }
};

/**
 * Format month and year in ET
 * @param {string|Date} isoUtc - ISO 8601 timestamp or Date object
 * @returns {string} Formatted as "Jan 2025"
 */
export const formatMonthYearET = (isoUtc) => {
  if (!isoUtc) return '';
  try {
    const utcTimestamp = ensureUtcTimestamp(isoUtc);
    return formatInTimeZone(utcTimestamp, ACCOUNT_TZ, 'MMM yyyy');
  } catch (error) {
    return '';
  }
};

/**
 * Format a numeric value as currency with 2 decimal places.
 *
 * Negative-zero guard (Feb 19, 2026): floating-point math from discount
 * calculations can produce values like -0.001 whose `toFixed(2)` would
 * render as "-0.00". We clamp any value whose absolute magnitude rounds
 * to zero at two decimals (|v| < 0.005) back to a true positive 0 so
 * tax certificate notes/UI never display "-0.00" or "$-0.00".
 *
 * @param {string|number} value - The value to format
 * @returns {string} Formatted currency string (e.g., "500.00")
 */
export const formatCurrency = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const num = typeof value === 'string' ? parseFloat(value.replace(/[^0-9.-]/g, '')) : value;
  if (isNaN(num)) return '';
  const safe = Math.abs(num) < 0.005 ? 0 : num;
  return safe.toFixed(2);
};

/**
 * Parse and normalize a currency input value
 * Removes non-numeric characters except decimal point and negative sign
 * @param {string} value - Raw input value
 * @returns {string} Normalized numeric string
 */
export const parseCurrencyInput = (value) => {
  if (!value) return '';
  return value.replace(/[^0-9.-]/g, '');
};

/**
 * Handle currency field change - allows typing while preserving numeric value
 * @param {string} value - Input value from user
 * @returns {string} Cleaned numeric string for state
 */
export const handleCurrencyChange = (value) => {
  if (!value) return '';
  const cleaned = value.replace(/[^0-9.-]/g, '');
  const parts = cleaned.split('.');
  if (parts.length > 2) {
    return parts[0] + '.' + parts.slice(1).join('');
  }
  return cleaned;
};

/**
 * Handle currency field blur - format to 2 decimal places
 * @param {string} value - Current field value
 * @returns {string} Formatted currency value with 2 decimals
 */
export const handleCurrencyBlur = (value) => {
  if (!value || value === '' || value === '-') return '';
  const num = parseFloat(value);
  if (isNaN(num)) return '';
  return num.toFixed(2);
};

/**
 * Get tooltip info for timestamp (shows both ET and UTC for audit clarity)
 * @param {string} isoUtc - ISO 8601 timestamp with Z (UTC)
 * @returns {string} Tooltip text with ET time and UTC for verification
 */
export const getTimestampTooltip = (isoUtc) => {
  if (!isoUtc) return '';
  
  try {
    const etTime = formatUtcToET(isoUtc);
    const utcTime = typeof isoUtc === 'string' ? isoUtc : new Date(isoUtc).toISOString();
    return `${etTime}\nUTC: ${utcTime}`;
  } catch (error) {
    console.error('Error creating tooltip:', error);
    return '';
  }
};

/**
 * Format property address with county and state for enhanced display
 * Used in both dashboard and reports modules
 * @param {Object} order - Order object containing property data
 * @returns {string} Formatted address in "Address, County, State" format
 */
export const formatPropertyAddress = (order) => {
  if (!order) return 'N/A';
  
  try {
    if (order.property_address_display) {
      return order.property_address_display;
    }
    
    const parts = [];
    const street = order.property_address || order.property_address_full || order.property_address_line1;
    if (street && street !== 'N/A') {
      parts.push(street);
    }
    
    if (order.property_city && order.property_city !== 'N/A') {
      parts.push(order.property_city);
    }
    
    const state = order.property_state || order.state;
    if (state && state !== 'N/A') {
      const zip = order.property_zip || order.property_zip_code;
      if (zip && zip !== 'N/A') {
        parts.push(`${state} ${zip}`);
      } else {
        parts.push(state);
      }
    }
    
    return parts.length > 0 ? parts.join(', ') : 'N/A';
    
  } catch (error) {
    console.error('Error formatting property address:', error);
    return order?.property_address || 'N/A';
  }
};

/**
 * Convert a jurisdiction name (or any proper-noun string) to Title Case.
 * Handles ALL-CAPS input, mixed casing, and keeps small words lowercase
 * (except when they appear at the start of the string).
 *
 * Examples:
 *   "NEWARK"              → "Newark"
 *   "NEW CASTLE COUNTY"   → "New Castle County"
 *   "city of newark"      → "City of Newark"
 *   "SUFFOLK COUNTY"      → "Suffolk County"
 *   ""                    → ""
 *
 * @param {string} name - Raw jurisdiction name
 * @returns {string} Title-cased jurisdiction name
 */
const SMALL_WORDS = new Set(['of', 'the', 'and', 'in', 'at', 'by', 'for', 'on', 'to']);

export const toTitleCase = (name) => {
  if (!name || typeof name !== 'string') return name || '';
  return name
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .map((word, idx) => {
      if (idx > 0 && SMALL_WORDS.has(word)) return word;
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
};

/**
 * PJR-001 (Oct 2026) — display-only Title Case for jurisdiction / collector /
 * payee names. NEVER used on stored values or form inputs.
 *   "hash white"        → "Hash White"
 *   "city of newark"    → "City of Newark"
 *   "NEWARK CITY"       → "Newark City"
 *   "Washington DC"     → "Washington DC"     (acronym preserved)
 *   "HARRIS COUNTY MUD 12" → "Harris County MUD 12"
 *   "McDonald Township" → "McDonald Township" (mixed-case word preserved)
 */
const DISPLAY_ACRONYMS = new Set([
  'LLC', 'INC', 'LLP', 'LP', 'LTD', 'PLLC', 'PC', 'CO', 'CORP', 'USA', 'US', 'DBA', 'TR', 'JR', 'SR',
  'II', 'III', 'IV', 'VI', 'VII', 'VIII', 'IX',
  'DC', 'NYC', 'MUD', 'PID', 'PUD', 'ISD', 'CDD', 'HOA', 'POA', 'LID', 'WCID', 'FWSD', 'MMD', 'ESD', 'TIF', 'UD', 'CSD', 'RD', 'FD', 'WD', 'SD',
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO',
  'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
]);
const DISPLAY_SMALL_WORDS = new Set(['of', 'the', 'and', 'in', 'at', 'by', 'for', 'on', 'to', 'de', 'la', 'del', 'du', 'des', 'von', 'van', 'der']);

const _displayCaseWord = (word, idx) => {
  if (!word) return word;
  const bare = word.replace(/[.,;:()]+$/, '');
  if (/\d|[&/]/.test(word)) return word;
  if (/[a-z][A-Z]/.test(word)) return word; // McDonald, DeKalb — user intent preserved
  if (DISPLAY_ACRONYMS.has(bare.toUpperCase())) return word.toUpperCase();
  if (word === word.toUpperCase() && !/\.$/.test(word) && bare.length <= 3 && !/[AEIOU]/.test(bare)) return word; // short vowel-less caps (DC, NYC); "ST." still → "St."
  const lower = word.toLowerCase();
  if (idx > 0 && DISPLAY_SMALL_WORDS.has(lower)) return lower;
  let out = lower.charAt(0).toUpperCase() + lower.slice(1);
  if (/^O'[a-z]/.test(out)) out = "O'" + out.charAt(2).toUpperCase() + out.slice(3);
  else if (/^D'[a-z]/.test(out)) out = "D'" + out.charAt(2).toUpperCase() + out.slice(3);
  return out;
};

export const displayTitleCase = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value !== 'string') return String(value);
  const text = value.trim().replace(/\s+/g, ' ');
  if (!text) return '';
  return text
    .split(' ')
    .map((word, idx) => word.split('-').map((part) => _displayCaseWord(part, idx)).join('-'))
    .join(' ');
};

/**
 * PJR-001 (Oct 2026) — conservative display-only US address formatting.
 * Reformats ONLY when the stored string strictly matches "street, city[,] ST[ ZIP]" (ST = valid 2-letter code);
 * anything else is returned exactly as stored (no guessing).
 *   "201 posum park road, newark De 19711" → "201 Posum Park Road, Newark, DE 19711"
 *   "201 posum park road, newark De"       → "201 Posum Park Road, Newark, DE"
 */
const US_STATE_CODES = new Set(['AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO',
  'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'PR', 'VI']);
const US_ADDRESS_RE = /^(.+?),\s*([A-Za-z][A-Za-z .'-]*?)\s*,?\s+([A-Za-z]{2})(?:\s+(\d{5}(?:-\d{4})?))?$/;

const ADDRESS_TOKEN_FIXES = { RD: 'Rd', Po: 'PO', 'P.o.': 'P.O.', Pob: 'POB', CO: 'Co' };
const _addressCase = (s) => displayTitleCase(s).split(' ').map((w) => ADDRESS_TOKEN_FIXES[w] || w).join(' ');

export const displayUSAddress = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value !== 'string') return String(value);
  const text = value.trim().replace(/\s+/g, ' ');
  const m = text.match(US_ADDRESS_RE);
  if (!m) return text;
  const [, street, city, st, zip] = m;
  if (!US_STATE_CODES.has(st.toUpperCase())) return text;
  return `${_addressCase(street)}, ${_addressCase(city)}, ${st.toUpperCase()}${zip ? ` ${zip}` : ''}`;
};

/**
 * DATE-DISPLAY-001 — global U.S. date-only standard: MM/DD/YYYY.
 * Bare date-only values ('2019-06-13', '6/13/2019', '06/13/2019') are
 * reformatted as strings — NEVER through timezone math, so the calendar
 * day can't shift. Full timestamps / Date objects use the account TZ (ET).
 * Unparseable input is returned unchanged.
 */
const ISO_DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
const US_DATE_ONLY = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;

export const formatDateOnly = (value) => {
  if (value === null || value === undefined || value === '') return '';
  if (value instanceof Date) return formatInTimeZone(value, ACCOUNT_TZ, 'MM/dd/yyyy');
  const s = String(value).trim();
  let m = ISO_DATE_ONLY.exec(s);
  if (m) return `${m[2]}/${m[3]}/${m[1]}`;
  m = US_DATE_ONLY.exec(s);
  if (m) return `${m[1].padStart(2, '0')}/${m[2].padStart(2, '0')}/${m[3]}`;
  if (/^\d{4}-\d{2}-\d{2}[T ]/.test(s)) {
    try {
      return formatInTimeZone(ensureUtcTimestamp(s), ACCOUNT_TZ, 'MM/dd/yyyy');
    } catch {
      return s;
    }
  }
  return s;
};

/**
 * DATE-DISPLAY-001 — date + time in the viewer's browser timezone:
 * "MM/DD/YYYY h:mm AM/PM". Used where a screen intentionally shows local
 * time (no ET suffix). For account-timezone timestamps use formatDateTimeET.
 */
export const formatDateTimeLocal = (value) => {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return String(value);
  return format(d, 'MM/dd/yyyy h:mm a');
};

/**
 * Format date for display - DEPRECATED, use formatDateTimeET instead
 * V1 REQUIREMENT: All timestamps must be in ET
 * @param {string} dateString - Date string to format
 * @returns {string} Formatted date string in ET
 */
export const formatDate = (dateString) => {
  if (!dateString) return '—';
  try {
    // Use ET timezone conversion instead of browser-local
    const utcTimestamp = ensureUtcTimestamp(dateString);
    return formatInTimeZone(utcTimestamp, ACCOUNT_TZ, 'MM/dd/yyyy HH:mm') + ' ET';
  } catch (error) {
    return '—';
  }
};
